import React, { useRef, Suspense } from 'react';
import PropTypes from 'prop-types';
import delve from 'packages/inmeta-epi-react/utils/delve';
import classnames from 'classnames';
import Heading, { H2, H3, TitleXL } from 'components/atoms/Heading';
import Container from 'components/atoms/Container';
import Text, { SmallText } from 'components/atoms/Text';
import { BookingButton, LinkButton } from 'components/atoms/Button/Button';
import FlexGrid from 'components/utilities/FlexGrid';
import TeaserMap from 'components/molecules/TeaserMap';
import Slider from 'components/molecules/ProductPresentationSlider';
import Tabs from 'components/molecules/ProductPresentationTabs';
import ProductContactInfo from 'components/molecules/ProductContactInfo';
import ProductOpeningHours from 'components/molecules/ProductOpeningHours';
import FavouriteButtonContainer from 'containers/molecules/FavouriteButtonContainer';

import AdvantageCard from 'components/atoms/AdvantageCard';
import AwardList from 'components/atoms/AwardList';
import Image from 'components/atoms/Image';
import useSiteSettings from 'hooks/use-site-settings';
import { BREAKPOINT_PHONE, BREAKPOINT_TABLET_PORTRAIT } from 'utils/breakpoint';
import { appendQueryParams } from 'utils/bundle-helper';
import { translate } from 'utils/translate';
import { getFormattedDate } from 'utils/dateTime';
import { getFileName, splitUrlName } from 'utils/getFileName';
import LoadingIndicator from 'components/utilities/LoadingIndicator';
import Icon from 'components/atoms/Icon';
import { grid, marker } from 'components/atoms/Icon/icons';
import EnturWidget from '../EnturWidget';

// product components
import Section from './Section';
import PriceTable from './PriceTable';
import RoomTable from './RoomTable';
import FacilityList from './FacilityList';
import BookATableWidget from './BookATableWidget';
import ExpandableDescription from './ExpandableDescription';

import * as styles from './Product.scss';
import Link from 'components/atoms/Link';
import SustainabilityTab from './SustainabilityTab';
import TagWrapper from 'components/molecules/TagWrapper';

const ProductEventList = React.lazy(() =>
  import(
    /* webpackChunkName: "producteventlist" */ /* webpackPreload: true */ 'components/molecules/ProductEventList'
  ),
);

const TripAdvisorWidget = React.lazy(() =>
  import(
    /* webpackChunkName: "tripadvisor" */ /* webpackPreload: true */
    'components/organisms/TripAdvisorWidget'
  ),
);

export const PRODUCTTYPE_NEWS = 'News';
export const PRODUCTTYPE_EVENT = 'Event';

const Product = ({
  product,
  url,
  breakpoint,
  hideEnturWidget,
  hideShortDescription,
  hidePriceInformation,
}) => {
  const isMobileView = breakpoint <= BREAKPOINT_PHONE;
  const isTabletView = breakpoint <= BREAKPOINT_TABLET_PORTRAIT;
  const isDesktopView = breakpoint > BREAKPOINT_TABLET_PORTRAIT;
  const {
    id,
    name,
    address,
    description,
    geoLocation,
    hierarchicalFacilityList,
    awardListWithNames,
    publicTransportDirections,
    upcomingEvents,
    openingHours,
    contactInfo,
    tripAdvisorRating,
    productType,
    published,
    ticketInfo,
    place,
    eventItems,
    bookingWebsite,
    rooms,
    myFavoritesId,
    advantageCardSettings,
    ecoSettings,
    externalSystemList,
    sustainability,
    tags,
  } = product;
  const { culture, colorSettings } = useSiteSettings();
  const isNews = productType === PRODUCTTYPE_NEWS;
  const isEvent = productType === PRODUCTTYPE_EVENT;
  const queryKey = 'show';
  const tripAdvisorId = delve(tripAdvisorRating, 'tripAdvisorId', false);
  const hasMultipleImages = !!product.images && product.images.length > 1;
  const hasImages = !!product.images && product.images.length > 0;
  const hasContactInfo = !!contactInfo;
  const showEventList = !isNews && !!eventItems && !!eventItems.length;
  const showOpeningHours =
    !isNews && !!openingHours && !!openingHours.length && !showEventList;
  const showMainInfo = !!description && isMobileView;
  const showFacilities =
    !!hierarchicalFacilityList && !!hierarchicalFacilityList.length;
  const showMapTransport = !!geoLocation || !!publicTransportDirections;
  const bookATable =
    externalSystemList &&
    externalSystemList.find(
      external => external.externalSystem === 'Bookatable',
    );
  const isSustainable = sustainability.sustainabilityGroups.length
    ? true
    : false;

  const readmoreLink = showMainInfo
    ? appendQueryParams(url, { [queryKey]: 'main-info' }).href
    : null;

  const routes = product.routes.filter(route => !!route.url);
  const gpxRoute =
    routes.find(
      route => splitUrlName(route.url).ending.toLowerCase() === 'gpx',
    ) || null;

  const productColor =
    colorSettings.productColors[productType.toLowerCase()] ||
    colorSettings.productColors.others;

  const mapRef = useRef();
  const sliderRef = useRef();
  const scrollToRef = ref => {
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const openingHoursLabel = () => {
    const showHours = showOpeningHours || showEventList;
    const showPrices = !!ticketInfo || !!rooms;
    if (showHours && showPrices) return translate('/product/openingHoursTab');
    if (showHours) return translate('/product/openingHours');
    return translate('/product/prices');
  };
  const showTabs =
    (!!showMainInfo && !isNews) ||
    showOpeningHours ||
    showEventList ||
    (!!ticketInfo && ticketInfo.prices) ||
    (!!rooms && rooms.length > 0) ||
    showFacilities ||
    !!upcomingEvents ||
    !!tripAdvisorId;
  return (
    <Container>
      {hasImages && isTabletView && (
        <Suspense fallback={<LoadingIndicator />}>
          <Slider
            className={styles.presentationSlider}
            product={product}
            type="slide"
            color={productColor}
          />
        </Suspense>
      )}

      {!!name && (
        <TitleXL underline={productColor} className={styles.title}>
          {name}
        </TitleXL>
      )}
      {!hideShortDescription && description && description.shortPlainText && (
        <p className={styles.intro}>{description.shortPlainText}</p>
      )}
      {hasImages && isDesktopView && (
        <React.Fragment>
          <Image
            src={product.images[0].url}
            ratio={[3, 2]}
            fit="cover"
            cropDetails={product.images[0].cropDetails || []}
            copy={product.images[0].copyright}
            lazy={false}
            description={product.images[0].name}
            className={styles.mainImage}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '2rem',
            }}
          >
            {hasMultipleImages && (
              <LinkButton
                className={styles.seeMoreImages}
                onClick={() => scrollToRef(sliderRef)}
              >
                {translate('/pages/productListPage/showMoreImages')}
                <Icon icon={grid} />
              </LinkButton>
            )}
          </div>
        </React.Fragment>
      )}

      {isMobileView && (
        <React.Fragment>
          <ProductContactInfo
            className={styles.contactInfo}
            address={address}
            onAddressClick={() => scrollToRef(mapRef)}
            place={place}
          />
          <div className={styles.actions}>
            {!!bookingWebsite && (
              <BookingButton
                className={styles.bookingButton}
                href={bookingWebsite}
                target="_blank"
              >
                {isEvent
                  ? translate('/ticketInfo/findTickets')
                  : translate('/product/bookNow')}
              </BookingButton>
            )}

            {!!myFavoritesId && (
              <FavouriteButtonContainer
                className={styles.favouriteButton}
                identifier={myFavoritesId}
              />
            )}
          </div>
        </React.Fragment>
      )}

      <FlexGrid gutter={isTabletView ? '24px' : '48px'} collapse={isMobileView}>
        <FlexGrid.Column width="64%" className={styles.gridColumn} shrink>
          {!!advantageCardSettings && (
            <AdvantageCard color={productColor} {...advantageCardSettings} />
          )}
          {showTabs && (
            <Tabs
              current={delve(url, `query.${queryKey}`)}
              scrollIntoView={isMobileView}
              mode={Tabs.modes.accordion}
              getUrlForTab={id =>
                appendQueryParams(url, {
                  [queryKey]: id || 'none',
                }).href
              }
            >
              {!!showMainInfo && !isNews && (
                <Tabs.Tab label={translate('/product/mainTab')} id="main-info">
                  {(!!description || !!ecoSettings) && isMobileView && (
                    <Section
                      accessibleTitle={!isNews && translate('/product/about')}
                    >
                      {!!description && (
                        <ExpandableDescription
                          truncatedLength={500}
                          className={styles.description}
                          html={description.longRichText}
                          text={description.longPlainText}
                        />
                      )}
                      {isNews && (
                        <SmallText
                          as="time"
                          className={styles.published}
                          dateTime={getFormattedDate(published, 'yyyy-MM-dd')}
                        >
                          {`${translate(
                            '/product/publishedDate',
                          )} ${getFormattedDate(published, 'P')}`}
                        </SmallText>
                      )}
                      {ecoSettings && <AwardList {...ecoSettings} />}

                      <ProductContactInfo
                        className={styles.contactInfo}
                        contactInfo={contactInfo}
                        externalSystemList={externalSystemList}
                      />
                    </Section>
                  )}
                </Tabs.Tab>
              )}
              {(showOpeningHours ||
                showEventList ||
                (!!ticketInfo && ticketInfo.prices && !hidePriceInformation) ||
                (!!rooms && rooms.length > 0)) && (
                <Tabs.Tab label={openingHoursLabel()} id="openinghours">
                  {showOpeningHours && (
                    <Section className={styles.openingHours}>
                      <ProductOpeningHours
                        openingHours={openingHours}
                        product={product}
                      />
                    </Section>
                  )}

                  {showEventList && (
                    <Section className={styles.productEventList}>
                      <Suspense fallback={<LoadingIndicator />}>
                        <ProductEventList
                          products={eventItems}
                          showProductLink={false}
                          showInitial={3}
                          title={translate('/product/openingHours')}
                        />
                      </Suspense>
                    </Section>
                  )}
                  {!hidePriceInformation && !!ticketInfo && ticketInfo.prices && (
                    <Section
                      className={styles.prices}
                      title={translate('/product/prices')}
                    >
                      <PriceTable prices={ticketInfo.prices} />
                    </Section>
                  )}
                  {!!rooms && rooms.length > 0 && (
                    <Section
                      className={styles.rooms}
                      title={translate('/product/prices')}
                    >
                      <RoomTable rooms={rooms} />
                    </Section>
                  )}
                </Tabs.Tab>
              )}

              {showFacilities && (
                <Tabs.Tab
                  label={translate('/product/facilitiesTab')}
                  id="facilities"
                >
                  <FacilityList list={hierarchicalFacilityList} />
                </Tabs.Tab>
              )}
              {!!upcomingEvents && (
                <Tabs.Tab
                  label={translate('/product/upcomingEventsTab')}
                  id="upcoming-events"
                >
                  <Suspense fallback={<LoadingIndicator />}>
                    <Section>
                      <ProductEventList
                        products={upcomingEvents}
                        toggleGroups
                        groupByMonth
                      />
                    </Section>
                  </Suspense>
                </Tabs.Tab>
              )}
              {!!tripAdvisorId && (
                <Tabs.Tab
                  label={translate('/product/reviewsTab')}
                  id="reviews"
                  lazy
                >
                  <Suspense fallback={<LoadingIndicator />}>
                    <Section>
                      <TripAdvisorWidget
                        type={isMobileView ? 'summary' : 'detail'}
                        id={tripAdvisorId}
                        className={
                          !isDesktopView
                            ? styles.tripAdvisorSummaryWidget
                            : styles.tripAdvisorDetailWidget
                        }
                        culture={culture}
                      />
                    </Section>
                  </Suspense>
                </Tabs.Tab>
              )}

              {isSustainable && (
                <Tabs.Tab
                  label={sustainability.sustainabilityTabTitle}
                  id="sustainability"
                  className={styles.sustainabilityTab}
                >
                  <SustainabilityTab
                    sustainabilityData={sustainability}
                    productFacilities={product.facilityList}
                  ></SustainabilityTab>
                </Tabs.Tab>
              )}
            </Tabs>
          )}

          {(!!description || !!ecoSettings) && (!isMobileView || isNews) && (
            <Section
              className={classnames(styles.descriptionSection, {
                [styles.descriptionSectionSpacey]: showTabs,
              })}
              accessibleTitle={
                !isNews && `${translate('/product/about')} ${name}`
              }
            >
              {!!description && (
                <ExpandableDescription
                  html={description.longRichText}
                  text={description.longPlainText}
                />
              )}
              {isNews && (
                <SmallText
                  as="time"
                  className={styles.published}
                  dateTime={getFormattedDate(published, 'yyyy-MM-dd')}
                >
                  {`${translate('/product/publishedDate')} ${getFormattedDate(
                    published,
                    'P',
                  )}`}
                </SmallText>
              )}

              {ecoSettings && <AwardList {...ecoSettings} />}

              {isNews && (
                <ProductContactInfo
                  className={styles.contactInfo}
                  contactInfo={contactInfo}
                  externalSystemList={externalSystemList}
                />
              )}
            </Section>
          )}

          <TagWrapper data={tags} />
        </FlexGrid.Column>
        <FlexGrid.Column width="34%" className="gridColumn" grow>
          {!isMobileView && (
            <React.Fragment>
              {!!bookingWebsite && (
                <BookingButton
                  className={styles.bookingButton}
                  href={bookingWebsite}
                  target="_blank"
                >
                  {isEvent
                    ? translate('/ticketInfo/findTickets')
                    : translate('/product/bookNow')}
                </BookingButton>
              )}
              {!!bookATable && bookATable.id && (
                <BookATableWidget id={bookATable.id} />
              )}
              {!!myFavoritesId && (
                <FavouriteButtonContainer
                  className={styles.favouriteButton}
                  identifier={myFavoritesId}
                />
              )}
              {hasContactInfo && (
                <Section accessibleTitle={translate('/product/contact')}>
                  <ProductContactInfo
                    className={styles.contactInfo}
                    contactInfo={contactInfo}
                    address={address}
                    externalSystemList={externalSystemList}
                    place={place}
                    onAddressClick={() => scrollToRef(mapRef)}
                  />
                </Section>
              )}
              {routes && !!routes.length && (
                <Section>
                  <H2 alt size={Heading.sizes.heading5} gutterBottom>
                    {translate('/product/download')}
                  </H2>
                  {routes.map(route => (
                    <p className={styles.contactInfo}>
                      <a href={route.url}>{getFileName(route)}</a>
                    </p>
                  ))}
                </Section>
              )}
            </React.Fragment>
          )}
        </FlexGrid.Column>
      </FlexGrid>

      {hasMultipleImages && isDesktopView && (
        <div ref={sliderRef}>
          <Slider
            className={styles.presentationSlider}
            showThumbnailNavigation
            product={{ ...product, images: product.images.slice(1) }}
            type="fade"
          />
        </div>
      )}
      {!!bookATable && bookATable.id && isMobileView && (
        <BookATableWidget id={bookATable.id} />
      )}

      {showMapTransport && (
        <div ref={mapRef} className={styles.map}>
          <H2 alt size={Heading.sizes.heading2} gutterBottom>
            {translate('/product/mapTab')}
          </H2>
          {!!publicTransportDirections && (
            <p> {publicTransportDirections.longPlainText}</p>
          )}
          {!!geoLocation && !!address && (
            <FlexGrid gutter="0px" collapse={!isDesktopView}>
              <FlexGrid.Column grow>
                <TeaserMap
                  items={[
                    {
                      id,
                      title: name,
                      intro:
                        !!description &&
                        (description.shortPlainText ||
                          description.longPlainText),
                      geoLocation,
                    },
                  ]}
                  route={gpxRoute}
                  readmoreLink={readmoreLink}
                />
              </FlexGrid.Column>
              <FlexGrid.Column shrink>
                {!hideEnturWidget ? (
                  <EnturWidget
                    address={address}
                    geoLocation={geoLocation}
                    culture={culture}
                    color={productColor}
                    name={name}
                  />
                ) : (
                  <React.Fragment />
                )}
              </FlexGrid.Column>
            </FlexGrid>
          )}
        </div>
      )}
    </Container>
  );
};

Product.displayName = 'Product';
Product.propTypes = {
  product: PropTypes.object,
  url: PropTypes.object,
  breakpoint: PropTypes.number,
  hideEnturWidget: PropTypes.bool,
  hideShortDescription: PropTypes.bool,
};
Product.defaultProps = {};

export default Product;
